import { CoreModal, CoreModalProps } from '../../../_core/components/_ui/core-modal';
import { useCallback, useEffect, useRef, useState } from 'react';
import { enqueueSnackbar, useSnackbar } from 'notistack';
import { Box, Chip, Container, InputAdornment, MenuItem, Typography } from '@mui/material';
import { CoreButton } from '../../../_core/components/_ui/core-button';
import { mergeSx } from 'merge-sx';
import { buttonModalWindowStyles, chipDeleteIconStyles, mainTitleModalWindowStyles } from '../../../_core/styles';
import { useTranslation } from 'react-i18next';
import { useCreateFamilyArrayMutation, useGetStudentsOneRoasterQuery } from '../../../../store/api/families.api';
import { OneRosterFamiliesTable } from '../families-table';
import { CoreTextField } from '../../../_core/components/_ui/core-textfield';
import { useLanguageFilter, useMuiModal, useSearch } from '../../../_core/hooks';
import { ChipCloseIcon } from '../../../_core/components/icons';
import { CoreCheckbox } from '../../../_core/components/_ui/core-checkbox';
import { CoreOverflowTypographyTooltip } from '../../../_core/components/_ui/core-overflow-tooltip';
import { AutocompleteTextField, CoreAutoComplete } from '../../../_core/components/_ui/core-autocomplete';
import { Search } from '../../../_core/constants/icons.constants';
import { getObjectLanguage } from '../../../_core/components/languages-component';
import { CreateFamilyDto, LanguageEnum, OneRosterFamilyDto, SendMethodEnum } from '@families-link/shared';
import { FamilyFormModal, FamilyFormModalProps } from '../modal-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { RowSelectionState, Updater } from '@tanstack/react-table';
import { OneRosterSettingsFamilyModal, OneRosterSettingsFamilyModalProps } from '../modal-oneroster-settings-form/oneroster-settings-modal-form.component';
import { skipToken } from '@reduxjs/toolkit/query';

export type OneRosterFamilyImportModalProps = CoreModalProps & {
	formTitle?: string;
	defaultValues?: any;
	formError?: any;
	submitButtonName?: string;
	// submitAction(values: FormData): Promise<void>;
	closeModal(): void;
	districtId?: string | null;
};

export const OneRosterFamilyImportModal: React.FC<OneRosterFamilyImportModalProps> = ({
	closeModal,
	districtId,
	...modalProps
}) => {
	const [t] = useTranslation();
	const { register, search } = useSearch();
	const { register: registerLang, getValues: getValuesLang, control: controlLang, languages } = useLanguageFilter();
	const [createFamilyArray, { isLoading: loadingSubmit }] = useCreateFamilyArrayMutation();
	const { data: oneRosterStudentList, isFetching, isError, error } = useGetStudentsOneRoasterQuery(districtId ?? skipToken);

	const didShowErrorMessage = useRef(false)

	useEffect(() => {
		if (!isError) {
			didShowErrorMessage.current = false
			return	
		}

		if (didShowErrorMessage.current) return
		didShowErrorMessage.current = true

		const errorMessage = (error as any)?.data?.message

		enqueueSnackbar(errorMessage, {
			variant: 'error',
			autoHideDuration: 4000
		})
	}, [isError, error])

	const [filtereOneRosterStudentList, setFilteredOneRosterStudentList] = useState(oneRosterStudentList);

	useEffect(() => {
		if (search || languages?.length) {
			setFilteredOneRosterStudentList(
				oneRosterStudentList?.filter((oneRosterFamily: OneRosterFamilyDto) => {
					if(!search) return true
                    const regex = new RegExp(search, 'i');
					const isMatch =
						regex.test(oneRosterFamily?.familyName ?? '') ||
						regex.test(oneRosterFamily.studentFullName) ||
						regex.test(oneRosterFamily.studentEmail) ||
						regex.test(oneRosterFamily.studentId) ||
						regex.test(oneRosterFamily.studentPhoneNumber ?? '');
					return isMatch;
				}).filter((oneRosterFamily: OneRosterFamilyDto) =>
					languages?.length ? languages?.includes(oneRosterFamily.preferedLanguage) : true)
			);
		} else {
			setFilteredOneRosterStudentList(oneRosterStudentList);
		}
	}, [search, languages, oneRosterStudentList]);

	const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
	const [selectedOneRosterFamilies, setSelectedOneRosterFamilies] = useState<Record<string, OneRosterFamilyDto>>({});
	const onRowSelectionChange = (updater: Updater<RowSelectionState>) => {
		const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;

		setRowSelection(newSelection);

		setSelectedOneRosterFamilies((prev) => {
			const updated: Record<string, OneRosterFamilyDto> = {};

			Object.entries(newSelection).forEach(([id, isSelected]) => {
				if (isSelected) {
					const oneRosterFamily = oneRosterStudentList?.find((e: OneRosterFamilyDto) => e.studentId === id) || prev[id];
					if (oneRosterFamily) {
						updated[id] = oneRosterFamily;
					}
				}
			});

			return updated;
		});
	};

	const handleFileImport = async () => {
		const createFamilyDtoArray = Object.values(selectedOneRosterFamilies).map(
			(oneRosterFamily: OneRosterFamilyDto) => ({
				fullName: oneRosterFamily?.familyName ?? '',
				email: oneRosterFamily?.familyEmail ? oneRosterFamily?.familyEmail : oneRosterFamily.studentEmail,
				phoneNumber: oneRosterFamily?.familyPhoneNumber
					? oneRosterFamily?.familyPhoneNumber
					: oneRosterFamily?.studentPhoneNumber ?? '',
				location: oneRosterFamily?.location,
				preferedLanguage: [oneRosterFamily?.preferedLanguage],
				preferedSendMethod: SendMethodEnum.Sms,
				districtId: districtId ?? '',
				imageUrl: undefined,
				children: [{ name: oneRosterFamily?.studentName, studentId: oneRosterFamily?.studentId }],
			})
		);
		await createFamilyArray(createFamilyDtoArray);
		closeModal();
	};
	const { openModal: openFamilyPreviewForm, closeModal: closeFamilyPreviewForm } =
		useMuiModal<FamilyFormModalProps>(FamilyFormModal);
	const handlePreview = async (oneRosterFamily?: OneRosterFamilyDto) => {
		const defaultValues = {
			fullName:
				oneRosterFamily?.familyFullName && oneRosterFamily?.familyFullName !== '-'
					? oneRosterFamily?.familyFullName
					: oneRosterFamily?.familyName,
			email: oneRosterFamily?.familyEmail ? oneRosterFamily?.familyEmail : oneRosterFamily?.studentEmail,
			phoneNumber: oneRosterFamily?.familyPhoneNumber
				? oneRosterFamily?.familyPhoneNumber
				: oneRosterFamily?.studentPhoneNumber,
			location: oneRosterFamily?.location,
			preferedLanguage: [oneRosterFamily?.preferedLanguage],
			preferedSendMethod: SendMethodEnum.Sms,
			districtId,
			imageUrl: null,
			children: [{ name: oneRosterFamily?.studentName, studentId: oneRosterFamily?.studentId }],
		};
		const resolver = classValidatorResolver(CreateFamilyDto);

		openFamilyPreviewForm({
			formTitle: 'Preview Import Family',
			defaultValues,
			resolver,
			submitAction: async (values) => Promise.resolve(),
			closeModal: closeFamilyPreviewForm,
			hideControls: true,
		});
	};

	const { openModal: openModalOneRosterSettings, closeModal: closeModalOneRosterSettings } = 
		useMuiModal<OneRosterSettingsFamilyModalProps>(OneRosterSettingsFamilyModal);
	
	const handleOpenOneRosterSettings = () => {
		openModalOneRosterSettings({ closeModal: closeModalOneRosterSettings, districtId, isFirstImport: false });
	}

	return (
		<CoreModal {...modalProps} modalWidth={1050} bodySx={{ pt: 0 }} paperSx={{ width: '100%' }}>
			<Box sx={{ width: '100%', height: '100%' }}>
				<Typography variant="h5" sx={mainTitleModalWindowStyles}>
					{'Import OneRoster+ Family Contacts'}
				</Typography>

				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
					<Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
						<CoreTextField
							{...register('search')}
							placeholder={t('common.search')}
							hasEndAdornment
							endAdornment={
								<InputAdornment position="end">
									<Search />
								</InputAdornment>
							}
							sx={{ minWidth: '140px', maxWidth: '190px' }}
						/>
						<CoreAutoComplete
							multiple={true}
							{...registerLang('languages')}
							control={controlLang}
							controlSx={{
								minWidth: '180px',
								maxWidth: '240px',
							}}
							isFilter
							options={Object.values(LanguageEnum)}
							getOptionLabel={(option) => {
								const language = getObjectLanguage(option);

								return language ? language.name : '';
							}}
							selectOnFocus={true}
							isOptionEqualToValue={(option, value) => option === value}
							renderInput={(params) => (
								<AutocompleteTextField
									{...params}
									placeholder={!getValuesLang('languages')?.length ? t('common.filter-lang') : ''}
									sx={{
										'& .MuiOutlinedInput-root .MuiAutocomplete-input': {
											padding: '4.5px 4px 5.5px 5px',
											minWidth: 0,
										},
										'& .MuiInputBase-input': {
											height: '100%',
											padding: '0',
										},
									}}
								/>
							)}
							renderOption={(props, option, { selected }) => {
								const language = getObjectLanguage(option);

								return (
									<MenuItem
										{...props}
										key={language?.value}
										sx={{
											display: 'flex',
											alignItems: 'center',
										}}
									>
										<CoreCheckbox checked={selected} sx={{ mr: 1, p: 0, ml: 0 }} />
										<CoreOverflowTypographyTooltip>{language?.name}</CoreOverflowTypographyTooltip>
									</MenuItem>
								);
							}}
							renderTags={(value, getTagProps) => {
								const count = value.length - 1;

								return [
									value[0] && (
										<Chip
											sx={chipDeleteIconStyles}
											{...getTagProps({ index: 0 })}
											label={getObjectLanguage(value[0])?.name}
											deleteIcon={<ChipCloseIcon />}
										/>
									),
									count > 0 && (
										<Chip
											sx={chipDeleteIconStyles}
											{...getTagProps({ index: count })}
											label={`+${count}`}
											deleteIcon={<ChipCloseIcon />}
										/>
									),
								];
							}}
						/>
					</Box>
					<Box
						sx={{
							height: '250px',
							display: 'flex',
							borderRadius: '16px',
							border: '1px solid #DCDFE7',
							px: '24px',
							py: '12px',
						}}
					>
						<OneRosterFamiliesTable
							data={filtereOneRosterStudentList ?? []}
							loading={isFetching}
							onView={handlePreview}
							rowSelection={rowSelection}
							onRowSelectionChange={onRowSelectionChange}
						/>
					</Box>
				</Box>
				<Box
					sx={{
						width: '100%',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						mt: 5,
						gap: 3,
					}}
				>
					<CoreButton onClick={handleOpenOneRosterSettings} variant="secondary" sx={mergeSx(buttonModalWindowStyles, { mr: 'auto' })}>
						{t('families.forms.buttons.edit-oneroster-settings')}
					</CoreButton>
					<CoreButton onClick={closeModal} variant="secondary" sx={mergeSx(buttonModalWindowStyles)}>
						{t('families.forms.buttons.cancel')}
					</CoreButton>
					<CoreButton
						contraModeStyleButton
						loading={loadingSubmit}
						onClick={handleFileImport}
						sx={buttonModalWindowStyles}
					>
						{t('families.forms.buttons.import')}
					</CoreButton>
				</Box>
			</Box>
		</CoreModal>
	);
};
